import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiURL from '../Api/Api';
import { motion } from 'framer-motion';
import {
  UserIcon,
  LockClosedIcon,
  ArrowRightIcon,
  SparklesIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  RocketLaunchIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import logo from '../images/logo1.png'; // Adjust path as needed
import { DEFAULT_COMPANY_THEME, applyCompanyTheme, storeCompanyBranding } from '../utils/companyTheme';

const LoginForm = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${ApiURL}/login.php`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Invalid response format from server');
      }

      const data = await response.json();
      console.log('LoginForm: Response data:', data);
      
      if (data.status === 'success') {
        localStorage.setItem('role', data.role);
        localStorage.setItem('user_name', data.user_name);
        localStorage.setItem('id', data.id);
        localStorage.setItem('company_id', data.company_id || '');
        localStorage.setItem('package_info', JSON.stringify(data.package_info || null));
        localStorage.setItem('package_usage', JSON.stringify(data.package_usage || null));
        if (data.role === 'super_admin') {
          localStorage.removeItem('company_branding');
          localStorage.removeItem('company_name');
          localStorage.removeItem('company_theme_color');
          localStorage.removeItem('company_logo_path');
          applyCompanyTheme({ theme_color: DEFAULT_COMPANY_THEME });
        } else if (data.company_branding) {
          storeCompanyBranding(data.company_branding);
        } else if (data.package_info) {
          storeCompanyBranding(data.package_info);
        }
        console.log('LoginForm: Stored in localStorage:', {
          role: data.role,
          user_name: data.user_name,
          id: data.id,
          company_id: data.company_id,
          package_info: data.package_info,
        });
        
        if (onLogin) onLogin(data.role);
        setIsAnimating(true);
        
        setTimeout(() => {
          if (data.must_change_password) {
            navigate('/changepassword');
          } else if (data.role === 'super_admin') {
            navigate('/superadmin');
          } else if (data.role === 'worker' || data.role === 'admin') {
            navigate('/dashboard');
          } else {
            navigate('/dashboard');
          }
        }, 1500);
      } else {
        setError(data.message || 'Invalid credentials');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Login failed: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
      {/* Decorative blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 -left-40 w-96 h-96 bg-indigo-100/40 rounded-full blur-3xl" />
        <div className="absolute top-1/3 left-1/2 w-64 h-64 bg-cyan-100/20 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="max-w-6xl w-full mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            
            {/* Left Side - Hero Content */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="flex-1 hidden lg:block"
            >
              {/* Logo and Platform Badge Side by Side - Big Logo */}
              <div className="flex items-center gap-4 mb-8">
                <img src={logo} alt="Company Logo" className="h-24 w-auto" />
                <div className="inline-flex items-center gap-2 bg-blue-50/80 backdrop-blur-sm rounded-full px-4 py-2 border border-blue-100 shadow-sm">
                  <SparklesIcon className="w-4 h-4 text-blue-600" />
                  <span className="text-xs font-semibold text-blue-700 tracking-wide">TRANSACTION PLATFORM</span>
                </div>
              </div>
              
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-slate-900 leading-tight">
                Secure transaction <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-indigo-600">control for modern teams</span>
              </h1>
              <p className="mt-5 text-base lg:text-lg text-slate-500 max-w-lg leading-relaxed">
                Login to manage your company workspace, track fund flows, and monitor real-time ledgers with complete transparency.
              </p>
              
              <div className="mt-8 grid grid-cols-2 gap-4 max-w-xl">
                {[
                  { icon: ShieldCheckIcon, text: 'Secure authentication' },
                  { icon: ChartBarIcon, text: 'Real-time ledger' },
                  { icon: BuildingOfficeIcon, text: 'Multi-company support' },
                  { icon: UserGroupIcon, text: 'Role-based access' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-white rounded-xl shadow-sm border border-slate-100 px-4 py-3">
                    <item.icon className="w-5 h-5 text-indigo-500" />
                    <span className="text-sm font-medium text-slate-700">{item.text}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-slate-200">
                <p className="text-xs text-slate-400">
                  <span className="font-semibold text-slate-500">Super Admin Access:</span> superadmin / SuperAdmin@123
                </p>
              </div>
            </motion.div>

            {/* Right Side - Login Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className={`w-full max-w-md transition-all duration-500 ${
                isAnimating ? 'scale-110 opacity-0' : 'scale-100 opacity-100'
              }`}
            >
              <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
                {/* Card Header */}
                <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-white">Welcome Back</h2>
                      <p className="text-indigo-100 text-sm mt-0.5">Login to your account</p>
                    </div>
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                      <RocketLaunchIcon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6">
                  {/* Mobile Logo Section - No background div, clean layout */}
                  <div className="lg:hidden flex flex-col items-center mb-6">
                    <img src={logo} alt="Company Logo" className="h-20 w-auto mb-2" />
                    <div className="flex items-center gap-1.5">
                      <SparklesIcon className="w-3 h-3 text-blue-600" />
                      <span className="text-[10px] font-semibold text-blue-700 tracking-wide">TRANSACTION PLATFORM</span>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    {error && (
                      <motion.div 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="rounded-xl bg-red-50 border border-red-200 p-3"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs font-bold">!</div>
                          <p className="text-sm text-red-700">{error}</p>
                        </div>
                      </motion.div>
                    )}
                    
                    {/* Username Field */}
                    <div className="space-y-1.5">
                      <label htmlFor="username" className="block text-xs font-semibold text-slate-600 uppercase tracking-wide">
                        Username
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <UserIcon className="h-4 w-4 text-slate-400" />
                        </div>
                        <input
                          id="username"
                          type="text"
                          placeholder="Enter your username"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          className="block w-full pl-9 pr-3 py-3 text-base border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all bg-slate-50/50"
                          required
                        />
                      </div>
                    </div>
                    
                    {/* Password Field */}
                    <div className="space-y-1.5">
                      <label htmlFor="password" className="block text-xs font-semibold text-slate-600 uppercase tracking-wide">
                        Password
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <LockClosedIcon className="h-4 w-4 text-slate-400" />
                        </div>
                        <input
                          id="password"
                          type="password"
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="block w-full pl-9 pr-3 py-3 text-base border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all bg-slate-50/50"
                          required
                        />
                      </div>
                    </div>
                    
                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isLoading}
                      className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 shadow-md transition-all transform hover:scale-[1.02] active:scale-[0.98] ${
                        isLoading ? 'opacity-75 cursor-not-allowed' : ''
                      }`}
                    >
                      {isLoading ? (
                        <>
                          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Signing in...
                        </>
                      ) : (
                        <>
                          Log in
                          <ArrowRightIcon className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  </form>
                  
                  {/* Footer Links */}
                  <div className="mt-6 pt-5 border-t border-slate-100 text-center">
                    <button
                      type="button"
                      onClick={() => navigate('/signup')}
                      className="text-sm font-semibold text-indigo-600 hover:text-indigo-700 transition-colors"
                    >
                      Don't have an account? Create company account
                    </button>
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => navigate('/usermanual')}
                        className="text-xs font-semibold text-slate-500 hover:text-indigo-700 transition-colors"
                      >
                        User Manual
                      </button>
                    </div>
                    <div className="mt-3 text-[10px] text-slate-400">
                      Powered by Algo2bots Infotech
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Loading Animation Overlay */}
      {isAnimating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600"></div>
            <p className="mt-4 text-slate-600 font-medium">Redirecting...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginForm;
